import type {
    PreferencesPlugin,
    ConfigureOptions,
    GetOptions,
    GetResult,
    SetOptions,
    RemoveOptions,
    KeysResult,
} from './definitions';
import { HuaweiBridgeClient } from './huawei-bridge-client';

/**
 * PreferencesHuawei implements the same PreferencesPlugin interface as
 * @capacitor/preferences, routing calls through the Huawei JS Bridge.
 *
 * Data is stored in HarmonyOS native `preferences` storage, which is
 * persistent and not subject to the periodic localStorage clears the OS
 * may perform on WebViews. Stored data is cleared when the app is
 * uninstalled.
 *
 * The `group` from configure() maps to the native preferences store name
 * (default 'CapacitorStorage'), mirroring how the Android implementation
 * maps group -> SharedPreferences.
 */
export class PreferencesHuawei implements PreferencesPlugin {
    private bridge: HuaweiBridgeClient;

    constructor() {
        this.bridge = HuaweiBridgeClient.getInstance();
    }

    async configure(options: ConfigureOptions): Promise<void> {
        if (typeof options.group === 'string') {
            await this.bridge.call('preferencesConfigure', { group: options.group });
        }
    }

    async get(options: GetOptions): Promise<GetResult> {
        const res = await this.bridge.call('preferencesGet', { key: options.key });
        // Native omits `value` when the key is missing — normalize to null
        const value = res && res.value !== undefined && res.value !== null ? res.value : null;
        return { value };
    }

    /**
     * Set a value. Note: the native side removes the key when the value
     * is empty (cookie-like semantics) instead of storing an empty string.
     */
    async set(options: SetOptions): Promise<void> {
        await this.bridge.call('preferencesSet', { key: options.key, value: options.value });
    }

    async remove(options: RemoveOptions): Promise<void> {
        await this.bridge.call('preferencesRemove', { key: options.key });
    }

    async clear(): Promise<void> {
        await this.bridge.call('preferencesClear');
    }

    async keys(): Promise<KeysResult> {
        const res = await this.bridge.call('preferencesKeys');
        const keys = res && Array.isArray(res.keys) ? res.keys : [];
        return { keys };
    }
}
